const EXERCISE_VIDEO_CACHE_NAME = 'exercise-video-previews-v1';
const EXERCISE_VIDEO_THUMBNAIL_KEY_PREFIX = 'exercise-video-thumbnail:';
const THUMBNAIL_GENERATION_TIMEOUT_MILLISECONDS = 15000;

const pendingBlobRequests = new Map<string, Promise<Blob>>();

const createThumbnailStorageKey = (videoUrl: string): string => `${EXERCISE_VIDEO_THUMBNAIL_KEY_PREFIX}${encodeURIComponent(videoUrl)}`;

const fetchVideoBlob = async (videoUrl: string): Promise<Blob> => {
    const response = await fetch(videoUrl, { credentials: 'omit' });

    if (!response.ok) {
        throw new Error(`Unable to load video: ${response.status}`);
    }

    return response.blob();
};

export const getCachedExerciseVideoBlob = async (videoUrl: string): Promise<Blob> => {
    const existingRequest = pendingBlobRequests.get(videoUrl);

    if (existingRequest) {
        return existingRequest;
    }

    const request = (async () => {
        if (typeof window === 'undefined') {
            return fetchVideoBlob(videoUrl);
        }

        if (!('caches' in window)) {
            return fetchVideoBlob(videoUrl);
        }

        const cache = await window.caches.open(EXERCISE_VIDEO_CACHE_NAME);
        const cachedResponse = await cache.match(videoUrl);

        if (cachedResponse) {
            return cachedResponse.blob();
        }

        const response = await fetch(videoUrl, { credentials: 'omit' });

        if (!response.ok) {
            throw new Error(`Unable to load video: ${response.status}`);
        }

        await cache.put(videoUrl, response.clone());
        return response.blob();
    })();

    pendingBlobRequests.set(videoUrl, request);

    try {
        return await request;
    } finally {
        pendingBlobRequests.delete(videoUrl);
    }
};

export const getCachedExerciseVideoThumbnail = (videoUrl: string): string | undefined => {
    if (typeof window === 'undefined') {
        return undefined;
    }

    try {
        return window.localStorage.getItem(createThumbnailStorageKey(videoUrl)) || undefined;
    } catch (error) {
        return undefined;
    }
};

export const setCachedExerciseVideoThumbnail = (videoUrl: string, thumbnailDataUrl: string): void => {
    if (typeof window === 'undefined') {
        return;
    }

    try {
        window.localStorage.setItem(createThumbnailStorageKey(videoUrl), thumbnailDataUrl);
    } catch (error) {
        // Ignore storage quota and privacy mode failures.
    }
};

export const createExerciseVideoThumbnail = (videoUrl: string): Promise<string> => new Promise((resolve, reject) => {
    const videoElement = document.createElement('video');
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    videoElement.crossOrigin = 'anonymous';
    videoElement.muted = true;
    videoElement.playsInline = true;
    videoElement.preload = 'metadata';
    videoElement.src = videoUrl;

    timeoutId = setTimeout(() => {
        cleanup();
        reject(new Error('Thumbnail generation timed out.'));
    }, THUMBNAIL_GENERATION_TIMEOUT_MILLISECONDS);

    const handleLoadedData = () => {
        if (videoElement.duration && videoElement.duration > 0.1) {
            videoElement.currentTime = 0.1;
            return;
        }

        resolveThumbnail();
    };

    const handleSeeked = () => {
        resolveThumbnail();
    };

    const handleError = () => {
        cleanup();
        reject(new Error('Unable to generate thumbnail.'));
    };

    const cleanup = () => {
        if (timeoutId !== undefined) {
            clearTimeout(timeoutId);
            timeoutId = undefined;
        }
        videoElement.removeEventListener('loadeddata', handleLoadedData);
        videoElement.removeEventListener('seeked', handleSeeked);
        videoElement.removeEventListener('error', handleError);
        videoElement.pause();
        videoElement.removeAttribute('src');
        videoElement.load();
    };

    const resolveThumbnail = () => {
        try {
            const canvas = document.createElement('canvas');
            canvas.width = videoElement.videoWidth;
            canvas.height = videoElement.videoHeight;

            const context = canvas.getContext('2d');

            if (!context) {
                cleanup();
                reject(new Error('Unable to access canvas context.'));
                return;
            }

            context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
            const thumbnailDataUrl = canvas.toDataURL('image/jpeg', 0.82);

            cleanup();
            resolve(thumbnailDataUrl);
        } catch (error) {
            cleanup();
            reject(error as Error);
        }
    };

    videoElement.addEventListener('loadeddata', handleLoadedData);
    videoElement.addEventListener('seeked', handleSeeked);
    videoElement.addEventListener('error', handleError);
});
